// The Bets panel: bet slips styled as derby tickets, and the form that writes a new one.
import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CAT_IDS, type CatId } from '../race/types';
import {
  autoTitle, balances, draftProblems, leaders, MAX_EURO, MAX_PARTICIPANTS, MAX_RACES, payments,
  type Bet, type BetDraft, type BetMode,
} from './bets';
import { CAT_DISPLAY, catIcon } from './cats';
import { formatEuro, ordinal } from './format';
import { name, type HudStore } from './store';
import { TicketIcon } from './TicketIcon';

export const MODES: Record<BetMode, { icon: string; label: string; rule: string }> = {
  h2h: { icon: '⚔️', label: 'Head-to-head', rule: 'The best-placed cat among the bet’s cats takes each race’s point.' },
  wins: { icon: '🏆', label: 'Win points', rule: 'A point every time your cat wins the race outright.' },
};

const sideColour = (cats: CatId[]) => CAT_DISPLAY[cats[0] ?? 'nemo'].colour;
const orList = (items: string[]) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}` : items[0] ?? '');

function CatFace({ id, size = 34 }: { id: CatId; size?: number }) {
  const d = CAT_DISPLAY[id];
  return <img class="face" src={catIcon(id)} alt={d.short} title={d.name} width={size} height={size} style={{ background: d.colour }} />;
}

/** How long the bet lasts, in words. */
export function lengthText(bet: Pick<Bet, 'races' | 'participants'>): string {
  if (bet.races === null) return `Until ${orList(bet.participants.flatMap((p) => p.cats).map(name))} wins a race`;
  return bet.races === 1 ? 'One race' : `${bet.races} races`;
}

function Progress({ bet }: { bet: Bet }) {
  const byId = (id: string | null) => bet.participants.find((p) => p.id === id);
  if (bet.races === null) {
    const last = bet.log[bet.log.length - 1];
    const winner = bet.status === 'settled' && last ? byId(last.scorer) : undefined;
    const winCat = winner && last?.places.find((x) => x.place === 1)?.id;
    return (
      <p class="progress continuous">
        <span class="inf" aria-hidden="true">∞</span>
        {winner && winCat
          ? <span><strong>{name(winCat)}</strong> won after {bet.counted} race{bet.counted === 1 ? '' : 's'}: <strong>{winner.name}</strong> takes the bet.</span>
          : <span>{lengthText(bet)} · {bet.counted === 0 ? 'no races counted yet' : `${bet.counted} race${bet.counted === 1 ? '' : 's'} so far, no winner yet`}</span>}
      </p>
    );
  }
  const pips = Array.from({ length: bet.races }, (_, i) => bet.log[bet.log.length - bet.counted + i]);
  return (
    <div class="progress">
      <ol class="pips" aria-label={`${bet.counted} of ${bet.races} races counted`}>
        {pips.map((r, i) => {
          const who = r ? byId(r.scorer) : undefined;
          return (
            <li key={i} class={r ? (who ? 'won' : 'none') : 'todo'} style={who ? { '--pip': sideColour(who.cats) } : undefined}
              title={r ? `Race ${i + 1}: ${who ? `${who.name} takes the point` : 'no point'}` : `Race ${i + 1}: not run yet`}>
              {r ? (who ? who.name[0]!.toUpperCase() : '–') : i + 1}
            </li>
          );
        })}
      </ol>
      <span class="count">{bet.counted}/{bet.races}</span>
    </div>
  );
}

function RaceLog({ bet }: { bet: Bet }) {
  if (!bet.log.length) return null;
  const first = bet.counted - bet.log.length + 1;
  return (
    <details class="race-log">
      <summary>Races counted ({bet.counted})</summary>
      <ol>
        {[...bet.log].reverse().map((r, i) => {
          const who = bet.participants.find((p) => p.id === r.scorer);
          return (
            <li key={r.at}>
              <span class="no">Race {first + bet.log.length - 1 - i}</span>
              <span class="places">{r.places.map((x) => `${name(x.id)} ${x.place === null ? 'DSQ' : ordinal(x.place)}`).join(' · ')}</span>
              <span class="scorer">{who ? `→ ${who.name} +1` : 'no point'}</span>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

export function BetTicket({ bet, store }: { bet: Bet; store: HudStore }) {
  const [confirming, setConfirming] = useState(false);
  const bal = balances(bet);
  const top = leaders(bet).map((p) => p.id);
  const pay = payments(bet);
  const pname = (id: string) => bet.participants.find((p) => p.id === id)?.name ?? '?';
  const mode = MODES[bet.mode];
  const stamp = bet.status === 'paused' ? 'Paused' : bet.status === 'settled' ? 'Settled' : bet.counted === 0 ? 'Next race' : 'Running';
  return (
    <article class={`ticket ${bet.status}`} aria-label={`Bet: ${bet.title}`}>
      <div class="stub" aria-hidden="true">
        <span class="mode-icon">{bet.races === null ? '∞' : mode.icon}</span>
        <span class="mode">{bet.races === null ? 'Continuous' : mode.label}</span>
        <span class="value">1 pt<br /><strong>{formatEuro(bet.euroPerPoint)}</strong></span>
      </div>
      <div class="body">
        <header>
          <h3>{bet.title}</h3>
          <span class={`stamp-${bet.status}`} role="status">{stamp}</span>
        </header>
        <p class="terms">
          {bet.races === null ? `Continuous · ${mode.label}` : mode.label} · {lengthText(bet)} · 1 point = {formatEuro(bet.euroPerPoint)}
        </p>
        <div class="sides">
          {bet.participants.map((p, i) => (
            <Fragment key={p.id}>
              {i > 0 && <span class="vs" aria-hidden="true">vs</span>}
              <div class={`side${top.includes(p.id) ? ' ahead' : ''}`} style={{ '--side': sideColour(p.cats) }}>
                <div class="faces">{p.cats.map((c) => <CatFace key={c} id={c} />)}</div>
                <div class="who">{p.name}{top.includes(p.id) && top.length === 1 && <span class="crown" aria-label={bet.status === 'settled' ? 'winner' : 'ahead'}> 👑</span>}</div>
                <div class="pts" aria-label={`${bet.points[p.id] ?? 0} points`}>{bet.points[p.id] ?? 0}<small> pt</small></div>
                <div class={`money ${bal[p.id]! > 0 ? 'up' : bal[p.id]! < 0 ? 'down' : ''}`}>{formatEuro(bal[p.id]!, true)}</div>
              </div>
            </Fragment>
          ))}
        </div>
        <Progress bet={bet} />
        <p class="settle">
          {pay.length === 0
            ? (bet.counted === 0 ? 'Counts live races that start after the bet is placed or resumed.' : 'All square so far.')
            : <>{bet.status === 'settled' ? 'Settle up: ' : 'If it ended now: '}{pay.map((x, i) => <span key={i}>{pname(x.from)} pays {pname(x.to)} <strong>{formatEuro(x.amount)}</strong></span>)}</>}
        </p>
        <RaceLog bet={bet} />
        <footer>
          {bet.status === 'active' && <button type="button" onClick={() => store.pauseBet(bet.id)}>⏸ Pause</button>}
          {bet.status === 'paused' && <button type="button" class="on" onClick={() => store.resumeBet(bet.id)}>▶ Resume</button>}
          <button type="button" class={confirming ? 'danger' : ''} onBlur={() => setConfirming(false)}
            onClick={() => (confirming ? store.deleteBet(bet.id) : setConfirming(true))}>{confirming ? 'Really delete?' : 'Delete'}</button>
        </footer>
      </div>
    </article>
  );
}

const emptyDraft = (): BetDraft => ({ title: '', mode: 'h2h', euroPerPoint: 1, races: 1, participants: [{ name: '', cats: [] }, { name: '', cats: [] }] });
const QUICK_EUROS = [0.5, 1, 5, 10];

export function BetForm({ store, onDone }: { store: HudStore; onDone: () => void }) {
  const [d, setD] = useState<BetDraft>(emptyDraft);
  const [racesInput, setRacesInput] = useState(1);
  const [tried, setTried] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => { firstRef.current?.focus(); }, []);
  const problems = draftProblems(d);
  const set = (patch: Partial<BetDraft>) => setD({ ...d, ...patch });
  const setP = (i: number, patch: Partial<BetDraft['participants'][number]>) =>
    set({ participants: d.participants.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const toggleCat = (i: number, c: CatId) => {
    const p = d.participants[i]!;
    setP(i, { cats: p.cats.includes(c) ? p.cats.filter((x) => x !== c) : [...p.cats, c] });
  };
  const setRaces = (n: number) => { const v = Math.min(MAX_RACES, Math.max(1, Math.round(n) || 1)); setRacesInput(v); set({ races: v }); };
  const submit = (e: Event) => {
    e.preventDefault();
    setTried(true);
    if (problems.length) return;
    store.addBet(d);
    onDone();
  };
  const sides = d.participants.filter((p) => p.cats.length);
  const allCats = d.participants.flatMap((p) => p.cats);
  return (
    <form class="bet-form" onSubmit={submit} aria-label="New bet">
      <fieldset class="modes">
        <legend>Game mode</legend>
        {(Object.keys(MODES) as BetMode[]).map((m) => (
          <label key={m} class={`mode-card${d.mode === m ? ' on' : ''}`}>
            <input type="radio" name="mode" value={m} checked={d.mode === m} onChange={() => set({ mode: m })} />
            <span class="mode-icon" aria-hidden="true">{MODES[m].icon}</span>
            <span><strong>{MODES[m].label}</strong><small>{MODES[m].rule}</small></span>
          </label>
        ))}
      </fieldset>

      <div class="row-2">
        <fieldset class="length">
          <legend>How long</legend>
          <label class={d.races !== null ? 'on' : ''}>
            <input type="radio" name="length" checked={d.races !== null} onChange={() => set({ races: racesInput })} />
            <span class="stepper">
              <button type="button" aria-label="Fewer races" disabled={d.races === null} onClick={() => setRaces(racesInput - 1)}>−</button>
              <input type="number" min={1} max={MAX_RACES} value={racesInput} aria-label="Number of races" disabled={d.races === null}
                onInput={(e) => setRaces(Number((e.currentTarget as HTMLInputElement).value))} />
              <button type="button" aria-label="More races" disabled={d.races === null} onClick={() => setRaces(racesInput + 1)}>+</button>
            </span>
            {racesInput === 1 ? 'race' : 'races'}
          </label>
          <label class={d.races === null ? 'on' : ''}>
            <input type="radio" name="length" checked={d.races === null} onChange={() => set({ races: null })} />
            <span><strong>∞ Continuous</strong><small>Runs until one of the bet’s cats wins a race. That race decides it, in either mode.</small></span>
          </label>
        </fieldset>
        <fieldset class="value">
          <legend>One point is worth</legend>
          <label class="euro">
            <input type="number" min={0} max={MAX_EURO} step={0.5} value={d.euroPerPoint} aria-label="Euros per point"
              onInput={(e) => set({ euroPerPoint: Number((e.currentTarget as HTMLInputElement).value) })} /> €
          </label>
          <div class="quick">
            {QUICK_EUROS.map((v) => (
              <button key={v} type="button" class={d.euroPerPoint === v ? 'on' : ''} aria-pressed={d.euroPerPoint === v} onClick={() => set({ euroPerPoint: v })}>{formatEuro(v)}</button>
            ))}
          </div>
          <small>For every point between two players, the one behind pays the one ahead.</small>
        </fieldset>
      </div>

      <fieldset class="players">
        <legend>Participants and their cats</legend>
        {d.participants.map((p, i) => (
          <div key={i} class="player" style={{ '--side': p.cats.length ? sideColour(p.cats) : 'rgba(255,243,220,0.25)' }}>
            <div class="player-head">
              <input type="text" ref={i === 0 ? firstRef : undefined} value={p.name} maxLength={24} placeholder={`Participant ${i + 1}`}
                aria-label={`Participant ${i + 1} name`} onInput={(e) => setP(i, { name: (e.currentTarget as HTMLInputElement).value })} />
              {d.participants.length > 2 && (
                <button type="button" class="remove" aria-label={`Remove participant ${i + 1}`}
                  onClick={() => set({ participants: d.participants.filter((_, j) => j !== i) })}>✕</button>
              )}
            </div>
            <div class="cat-picks" role="group" aria-label={`Cats for ${p.name.trim() || `participant ${i + 1}`}`}>
              {CAT_IDS.map((c) => {
                const mine = p.cats.includes(c);
                const owner = d.participants.findIndex((q, j) => j !== i && q.cats.includes(c));
                const taken = owner >= 0;
                const takenBy = taken ? (d.participants[owner]!.name.trim() || `Participant ${owner + 1}`) : '';
                return (
                  <button key={c} type="button" role="checkbox" aria-checked={mine} disabled={taken} class={`pick${mine ? ' on' : ''}`}
                    style={{ '--cat': CAT_DISPLAY[c].colour }} title={taken ? `${CAT_DISPLAY[c].name} is ${takenBy}’s` : CAT_DISPLAY[c].name}
                    onClick={() => toggleCat(i, c)}>
                    <img src={catIcon(c)} alt="" width={36} height={36} />
                    <span>{CAT_DISPLAY[c].short}</span>
                    {taken && <small>{takenBy}</small>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {d.participants.length < MAX_PARTICIPANTS && allCats.length < CAT_IDS.length && (
          <button type="button" class="add" onClick={() => set({ participants: [...d.participants, { name: '', cats: [] }] })}>+ Add participant</button>
        )}
      </fieldset>

      <label class="title-field">
        <span>Name the bet <small>(optional)</small></span>
        <input type="text" maxLength={40} value={d.title} placeholder={autoTitle(d, name)} onInput={(e) => set({ title: (e.currentTarget as HTMLInputElement).value })} />
      </label>

      <p class="preview" aria-live="polite">
        {sides.length >= 2
          ? <>{sides.map((p, i) => <span key={i}>{i > 0 && ' vs '}<strong>{p.name.trim() || `Participant ${d.participants.indexOf(p) + 1}`}</strong> ({p.cats.map(name).join(' & ')})</span>)}
            {' · '}{d.races === null ? `until ${orList(allCats.map(name))} wins` : d.races === 1 ? 'one race' : `${d.races} races`}
            {' · '}1 point = {Number.isFinite(d.euroPerPoint) ? formatEuro(d.euroPerPoint) : '?'}. Starts with the next live race.</>
          : 'Pick cats for at least two participants.'}
      </p>
      {tried && problems.length > 0 && <ul class="problems" role="alert">{problems.map((x) => <li key={x}>{x}</li>)}</ul>}
      <footer>
        <button type="button" onClick={onDone}>Cancel</button>
        <button type="submit" class="place"><TicketIcon /> Place bet</button>
      </footer>
    </form>
  );
}

type Tab = 'open' | 'settled';

export function BetsPanel({ store }: { store: HudStore }) {
  const [tab, setTab] = useState<Tab>('open');
  const [writing, setWriting] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = store.betsOpen.value;
  useEffect(() => { if (open) closeRef.current?.focus(); else setWriting(false); }, [open]);
  if (!open) return null;
  const bets = store.bets.value;
  const shown = bets.filter((b) => (tab === 'open' ? b.status !== 'settled' : b.status === 'settled')).reverse();
  const openCount = bets.filter((b) => b.status !== 'settled').length;
  const close = () => { store.betsOpen.value = false; };
  const replay = store.source.value.kind === 'replay';
  return (
    <section class="panel bets" role="dialog" aria-modal="false" aria-labelledby="bets-title">
      <header>
        <h2 id="bets-title"><TicketIcon /> {writing ? 'New bet' : 'Bets'}</h2>
        {!writing && (
          <div class="tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'open'} class={tab === 'open' ? 'on' : ''} onClick={() => setTab('open')}>Open ({openCount})</button>
            <button type="button" role="tab" aria-selected={tab === 'settled'} class={tab === 'settled' ? 'on' : ''} onClick={() => setTab('settled')}>Settled ({bets.length - openCount})</button>
          </div>
        )}
        {!writing && <button type="button" class="new" onClick={() => setWriting(true)}>+ New bet</button>}
        <button type="button" ref={closeRef} class="close" aria-label="Close bets" onClick={close}>✕</button>
      </header>
      {replay && <p class="note">This is a recorded replay, so it does not count for bets. Bets wait for the live race.</p>}
      <div class="scroll">
        {writing
          ? <BetForm store={store} onDone={() => { setWriting(false); setTab('open'); }} />
          : shown.length
            ? shown.map((b) => <BetTicket key={b.id} bet={b} store={store} />)
            : (
              <div class="empty">
                {tab === 'open'
                  ? <><p>No open bets. Pick your cats against a friend: head-to-head, win points, or a continuous bet that lasts until one of your cats wins.</p>
                    <button type="button" class="place" onClick={() => setWriting(true)}><TicketIcon /> Write the first bet</button></>
                  : <p>No settled bets yet.</p>}
              </div>
            )}
      </div>
      <footer><span class="hint">Saved in this browser only. Only live races count.</span></footer>
    </section>
  );
}
